import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';
import { Tabs, useToastContext, LoadingSpinner, Select, PageHeader, DescriptionList } from '@airclou/ui';
import { dashboardApiClient, TeamMember } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { ErrorState } from '../../components/state/ErrorState';
import { ValueSkeleton } from '../../components/state/ValueSkeleton';

// Type for workspace membership data
interface WorkspaceMembership {
  workspace_id: string;
  workspace_name: string;
  workspace_slug: string;
  workspace_role: 'admin' | 'member';
  joined_at: string;
  company: {
    id: string;
    name: string;
    slug: string;
  } | null;
  app_roles: Array<{
    app_id: string;
    app_name: string;
    app_slug: string;
    role: string;
  }>;
}

export function TeamMemberDetailsPage() {
  const { t } = useTranslation('dashboard');
  const { memberId, userId } = useParams<{ memberId?: string; userId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { success: toastSuccess } = useToastContext();
  const { user: currentUser } = useAuth();

  const [member, setMember] = useState<TeamMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [memberStatusSaving, setMemberStatusSaving] = useState(false);
  const [memberRoleSaving, setMemberRoleSaving] = useState(false);

  // Workspace memberships state
  const [workspaces, setWorkspaces] = useState<WorkspaceMembership[]>([]);
  // Starts true: the fetch is kicked off from an effect, so a `false` here
  // paints "no workspace memberships" for one frame before the request even
  // leaves.
  const [workspacesLoading, setWorkspacesLoading] = useState(true);
  const [workspacesError, setWorkspacesError] = useState<string | null>(null);

  // Which load is the current one. `/people/user/:userId` stays mounted across a
  // member switch — a URL edit, a bookmark, back/forward — so a slow answer for
  // the member we left can still be in flight, and writing it would put that
  // member's profile under the new member's URL, where the status and role
  // dropdowns write to `member.user_id`.
  const loadSeq = useRef(0);
  const workspacesSeq = useRef(0);
  // Who the page is currently showing. Every handler that writes state after an
  // await checks this, so an answer for the member we left cannot land on the
  // member now in the URL.
  // Written from the id-change effect, not during render: a render React starts
  // and abandons must not publish an id that never mounted. Every caller of the
  // loaders runs after a commit.
  const shownId = useRef(userId ?? memberId);

  const stillShowing = (id?: string) => id === shownId.current;

  const [activeTab, setActiveTab] = useState(() => {
    return location.pathname.endsWith('/workspaces') ? 'workspaces' : 'basic';
  });

  const loadMemberDetails = async () => {
    if (!userId && !memberId) return;

    const id = userId ?? memberId;
    // Bail before claiming a sequence number: a stale caller that took the newest
    // sequence and then failed its own id check would orphan the live load.
    if (!stillShowing(id)) return;

    const seq = ++loadSeq.current;
    try {
      setLoading(true);
      // `error` is cleared on success rather than up front, so the error state's
      // own Try Again doesn't unmount the thing that was just clicked.

      const response = userId
        ? await dashboardApiClient.getTeamMemberByUserId(userId)
        : await dashboardApiClient.getTeamMember(memberId!);
      if (seq !== loadSeq.current || !stillShowing(id)) return;

      if (response) {
        setMember(response);
        setError(null);
      } else {
        setError(t('teamMember.memberNotFoundOrDenied'));
      }
    } catch (err) {
      if (seq !== loadSeq.current || !stillShowing(id)) return;
      console.error('Failed to load member details:', err);
      setError(t('teamMember.failedToLoadMemberDetails'));
    } finally {
      // Only the current load owns the flag; a superseded one clearing it would
      // report the load that replaced it as finished.
      if (seq === loadSeq.current && stillShowing(id)) setLoading(false);
    }
  };

  const loadWorkspaces = async () => {
    if (!userId && !memberId) return;

    const id = userId ?? memberId;
    if (!stillShowing(id)) return;

    const seq = ++workspacesSeq.current;
    try {
      setWorkspacesLoading(true);
      setWorkspacesError(null);

      const response = userId
        ? await dashboardApiClient.getTeamMemberWorkspaces(userId)
        : await dashboardApiClient.getTeamMemberWorkspaces(memberId!);
      if (seq !== workspacesSeq.current || !stillShowing(id)) return;

      if (response) {
        setWorkspaces(response.workspaces);
      } else {
        setWorkspacesError(t('teamMember.failedToLoadWorkspaces'));
      }
    } catch (err) {
      if (seq !== workspacesSeq.current || !stillShowing(id)) return;
      console.error('Failed to load workspaces:', err);
      setWorkspacesError(t('teamMember.failedToLoadWorkspacesRetry'));
    } finally {
      if (seq === workspacesSeq.current && stillShowing(id)) setWorkspacesLoading(false);
    }
  };

  useEffect(() => {
    shownId.current = userId ?? memberId;
    // A different member under the same mounted route. Drop the previous one's
    // data first: without this, `member` stays set while the new id loads, so a
    // "no such member" answer for B left A's profile — and its enabled,
    // A-targeted status and role controls — on screen under B's URL.
    setMember(null);
    setError(null);
    setWorkspaces([]);
    setWorkspacesError(null);
    // These describe *this page* saving. The previous member's request keeps
    // going — its own guards stop it writing — but leaving the flags set disables
    // the new member's status and role controls until it finishes.
    setMemberStatusSaving(false);
    setMemberRoleSaving(false);
    loadMemberDetails();
    loadWorkspaces();
  }, [memberId, userId]);

  const handleTabChange = useCallback((tabId: string) => {
    const baseUrl = `/people/user/${userId ?? memberId}`;

    if (tabId === 'workspaces') {
      navigate(`${baseUrl}/workspaces`);
    } else {
      navigate(baseUrl);
    }
  }, [navigate, memberId, userId]);

  useEffect(() => {
    const newTab = location.pathname.endsWith('/workspaces') ? 'workspaces' : 'basic';
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [location.pathname]);

  const handleMemberStatusUpdate = async (newStatus: string) => {
    if (!member || newStatus === member.status) return;

    try {
      setMemberStatusSaving(true);
      setError(null);

      const pageId = userId ?? memberId;
      await dashboardApiClient.updateTeamMemberStatusByUserId(member.user_id, newStatus);
      // The write targets the member it captured. If the page has moved on,
      // stamping the new status into state would label whoever is on screen now
      // with it — the write is right, the display would not be.
      if (stillShowing(pageId)) {
        setMember(prev => prev ? { ...prev, status: newStatus as 'active' | 'inactive' | 'suspended' } : null);
      }
      toastSuccess(t('teamMember.memberStatusUpdated', { status: newStatus }), t('teamMember.statusUpdated'));
    } catch (err) {
      console.error('Failed to update member status:', err);
      // The banner belongs to the member the change was for; a switch mid-request
      // would otherwise show their failure on someone else's profile.
      if (stillShowing(userId ?? memberId)) setError(t('teamMember.failedToUpdateStatus'));
    } finally {
      setMemberStatusSaving(false);
    }
  };

  const handleMemberRoleUpdate = async (newRole: string) => {
    if (!member || newRole === member.role) return;
    await performRoleUpdate(newRole);
  };

  const performRoleUpdate = async (newRole: string) => {
    if (!member) return;
    const pageId = userId ?? memberId;

    try {
      setMemberRoleSaving(true);
      setError(null);

      await dashboardApiClient.updateTeamMemberRoleByUserId(member.user_id, newRole);
      if (stillShowing(pageId)) {
        setMember(prev => prev ? { ...prev, role: newRole as 'owner' | 'admin' | 'billing_admin' | 'viewer' } : null);
      }
      toastSuccess(t('teamMember.memberRoleUpdated', { role: newRole }), t('teamMember.roleUpdated'));
    } catch (err) {
      console.error('Failed to update member role:', err);
      if (stillShowing(pageId)) {
        // The role moved under this page (support made them the owner, say):
        // "try again" can never succeed on the stale role, so reload it.
        if ((err as { code?: string } | null)?.code === 'ROLE_CHANGED') {
          // Reload first: a successful load clears the banner, which would
          // take this explanation with it and leave the role flipping silently.
          await loadMemberDetails();
          if (stillShowing(pageId)) setError(t('teamMember.roleChangedReloaded'));
        } else {
          setError(t('teamMember.failedToUpdateRole'));
        }
      }
    } finally {
      setMemberRoleSaving(false);
    }
  };

  // Only "there is no member to show" replaces the page. An error raised while
  // the member is on screen — a failed status or role change — belongs in the
  // dismissible banner below, which the old `error || !member` gate made
  // unreachable: a failed role update swapped the whole page for an error state.
  // Keyed off `error` as well as `!loading`, so the error state survives its own
  // retry instead of flashing the skeleton shell mid-flight.
  if (!member && (error || !loading)) {
    return (
      <div className="space-y-6">
        <PageHeader title={t('teamMember.memberDetails')} />

        <ErrorState
          title={t('teamMember.memberDetails')}
          description={error || t('teamMember.memberNotFoundOrDenied')}
          onRetry={loadMemberDetails}
          isRetrying={loading}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title={t('teamMember.memberDetails')} />

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-300 text-sm">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-red-600 dark:text-red-400 text-sm underline mt-2"
          >
            {t('teamMember.dismiss')}
          </button>
        </div>
      )}

      <Tabs
        tabs={[
          {
            id: 'basic',
            label: t('teamMember.basic'),
            content: (
              <div className="max-w-2xl space-y-6" aria-busy={!member}>
                <DescriptionList
                  title={t('teamMember.profile')}
                  description={t('teamMember.profileDesc')}
                  items={[
                    {
                      term: t('teamMember.fullName'),
                      description: member ? (
                        <div className="truncate" title={member.name || member.email || t('organization.unknownUser')}>
                          {member.name ? (member.name.includes('@') ? member.name.split('@')[0] : member.name) : (member.email ? member.email.split('@')[0] : t('organization.unknownUser'))}
                        </div>
                      ) : (
                        <ValueSkeleton className="w-32" />
                      ),
                    },
                    {
                      term: t('teamMember.emailAddress'),
                      description: member ? (
                        <div className="truncate" title={member.email || t('organization.noEmail')}>
                          {member.email || t('organization.noEmail')}
                        </div>
                      ) : (
                        <ValueSkeleton className="w-48" />
                      ),
                    },
                    {
                      term: t('common.status'),
                      description: (
                        <div className="flex items-center gap-3">
                          {/* Rendered from the start, disabled until the
                              member's current status is known. */}
                          <Select
                            value={member?.status ?? ''}
                            onChange={handleMemberStatusUpdate}
                            options={[
                              { value: 'active', label: t('common.active') },
                              { value: 'inactive', label: t('common.inactive') },
                              { value: 'suspended', label: t('common.suspended') }
                            ]}
                            placeholder={t('common.loading')}
                            disabled={!member || memberStatusSaving}
                            className="w-36"
                          />
                          {memberStatusSaving && <LoadingSpinner size="small" />}
                        </div>
                      ),
                    },
                    {
                      term: t('teamMember.organizationRole'),
                      description: (
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-3">
                            {/* An organization has one owner, and only that owner can hand it
                                over (Settings > Organization > Transfer ownership). So 'owner'
                                is not on offer here, and the owner's own role is read-only. */}
                            <Select
                              value={member?.role ?? ''}
                              onChange={handleMemberRoleUpdate}
                              options={
                                member?.role === 'owner'
                                  ? [{ value: 'owner', label: t('common.owner') }]
                                  : [
                                      { value: 'admin', label: t('common.admin') },
                                      { value: 'billing_admin', label: t('roles.billingAdmin') },
                                      { value: 'viewer', label: t('common.viewer') }
                                    ]
                              }
                              placeholder={t('common.loading')}
                              disabled={!member || member.role === 'owner' || memberRoleSaving || currentUser?.role === 'viewer'}
                              className="w-40"
                            />
                            {memberRoleSaving && <LoadingSpinner size="small" />}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t(member?.role === 'owner' ? 'teamMember.ownerRoleLocked' : 'teamMember.orgRoleDesc')}
                          </p>
                        </div>
                      ),
                    },
                    {
                      term: t('teamMember.userId'),
                      description: member ? (
                        <code className="font-mono text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-gray-900 dark:text-gray-300">{member.user_id}</code>
                      ) : (
                        <ValueSkeleton className="w-52" />
                      ),
                    },
                  ]}
                />
              </div>
            )
          },
          {
            id: 'workspaces',
            label: t('common.workspaces'),
            content: (
              <div className="space-y-6">
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-foreground">{t('teamMember.workspaceMemberships')}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t('teamMember.workspaceMembershipsDesc')}
                  </p>
                </div>

                {workspacesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <LoadingSpinner />
                  </div>
                ) : workspacesError ? (
                  <ErrorState
                    title={t('teamMember.failedToLoadWorkspaces')}
                    description={workspacesError}
                    onRetry={loadWorkspaces}
                  />
                ) : workspaces.length === 0 ? (
                  <div className="bg-card border border-border rounded-xl p-12 text-center">
                    <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">{t('teamMember.noWorkspaceMemberships')}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('teamMember.noWorkspaceMembershipsDesc')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {workspaces.map(workspace => (
                      <div
                        key={workspace.workspace_id}
                        className="bg-card border border-border rounded-xl p-5"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-base font-semibold text-foreground">
                                {workspace.workspace_name}
                              </h3>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/15 text-primary dark:bg-primary/25 dark:text-[hsl(var(--primary-hover))]">
                                {workspace.workspace_role}
                              </span>
                            </div>
                            {workspace.company && (
                              <p className="text-sm text-muted-foreground mt-1">
                                {workspace.company.name}
                              </p>
                            )}
                          </div>
                          <Link
                            to={`/workspaces/${workspace.workspace_id}/users`}
                            className="text-sm text-primary hover:text-[hsl(var(--primary-hover))] hover:underline"
                          >
                            {t('teamMember.manageInWorkspace')} →
                          </Link>
                        </div>

                        {workspace.app_roles.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-border">
                            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                              {t('teamMember.appRoles')}
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {workspace.app_roles.map(appRole => (
                                <span
                                  key={appRole.app_id}
                                  className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-muted text-foreground/80"
                                >
                                  {appRole.app_name}: <span className="ml-1 font-semibold">{appRole.role}</span>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {workspace.app_roles.length === 0 && (
                          <div className="mt-4 pt-4 border-t border-border">
                            <p className="text-xs text-muted-foreground italic">
                              {t('teamMember.noAppRoles')}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          }
        ]}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

    </div>
  );
}
